import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import {
  AddressBook,
  ArrowLeft,
  CaretDown,
  Check,
  ClockCounterClockwise,
  Package,
  Plus,
  ShoppingCart,
  SignOut,
  Trash,
  UserCircle
} from '@phosphor-icons/react';
import { formatCny, hasDealerPermission, type CodeCategoryNode, type SessionUser } from '@longjie/shared';
import { ApiError, request } from './api';
import type { Address, CartDraft, CartDraftItem, CatalogCategory, CatalogResponse, DealerOrderHistory, Product, Sku } from './types';
import {
  BrandMark,
  Button,
  EmptyState,
  Field,
  LoadError,
  Modal,
  Money,
  StatusBadge,
  Toast
} from './components';
import { formatDateTime, fulfillmentLabel } from './ui-utils';
import { ProductCategoryFilter } from './ProductCategoryFilter';
import { useSynchronizedCodeCategories } from './code-category-sync';
import { DealerCatalogLayout } from './DealerCatalogLayout';
import { CatalogProductRow, CatalogQuantity } from './CatalogProductRow';
import { ProductImage } from './ProductImage';
import { catalogSelectionPath } from './catalog-selection';
import { requestDealerCatalog } from './dealer-catalog-request';
import { OwnPasswordModal } from './OwnPasswordModal';
import { FontSizeControl } from './FontSizeControl';
import { MobileHeaderControls } from './MobileHeaderControls';
import { MobileCategoryPicker } from './MobileCategoryPicker';
import { MobileSkuList } from './MobileSkuList';
import { usePhoneLayout } from './usePhoneLayout';
import { ProductSearch } from './ProductSearch';
import { useProductSearch } from './useProductSearch';
import { useCatalogRefresh } from './useCatalogRefresh';
import './dealer-history.css';
import './dealer-catalog.css';

type DealerTab = 'catalog' | 'orders' | 'addresses' | 'none';
type CartLine = { sku: Sku; product: Product; quantity: number };
type DraftStatus = 'loading' | 'saving' | 'saved' | 'error';
const CATALOG_PAGE_SIZE = 30;
const ORDERS_PAGE_SIZE = 20;
const PRICE_REFRESH_INTERVAL_MS = 60_000;

function draftItemSku(item: CartDraftItem): Sku {
  return {
    id: item.skuId, code: item.skuCode, productCode: item.productCode, specification: item.specification,
    unit: item.unit, priceCents: item.priceCents, productName: item.productName,
    ...(item.basePriceCents === undefined ? {} : { basePriceCents: item.basePriceCents }),
    ...(item.promotion ? { promotion: item.promotion } : {})
  };
}

function updatedSkuPrice(sku: Sku, pricing: Pick<Sku, 'priceCents' | 'basePriceCents' | 'promotion'>): Sku {
  const updated = { ...sku, priceCents: pricing.priceCents };
  delete updated.basePriceCents;
  delete updated.promotion;
  if (pricing.basePriceCents !== undefined) updated.basePriceCents = pricing.basePriceCents;
  if (pricing.promotion) updated.promotion = pricing.promotion;
  return updated;
}

function pricingSignature(sku: Sku) {
  return JSON.stringify([sku.priceCents, sku.basePriceCents, sku.promotion ?? null]);
}

function catalogPagePath(search: string, categoryId: string, page = 1, directCategory = false) {
  const path = catalogSelectionPath('/api/v1/catalog', search, categoryId, page, CATALOG_PAGE_SIZE);
  return directCategory && categoryId.startsWith('code:') && !search.trim() ? `${path}&categoryScope=direct` : path;
}

function currentDraftPayload(cart: Map<string, CartLine>, selectedAddressId: string, note: string) {
  return {
    selectedAddressId: selectedAddressId || null,
    note,
    items: [...cart.values()]
      .map((line) => ({ skuId: line.sku.id, quantity: line.quantity }))
      .sort((left, right) => left.skuId.localeCompare(right.skuId))
  };
}

function draftSignature(payload: ReturnType<typeof currentDraftPayload>) {
  return JSON.stringify(payload);
}

function errorDetailItems(details: unknown): Array<Record<string, unknown>> {
  if (!details || typeof details !== 'object') return [];
  const items = (details as { items?: unknown }).items;
  return Array.isArray(items)
    ? items.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object')
    : [];
}

function errorSkuIds(details: unknown): string[] {
  if (!details || typeof details !== 'object') return [];
  const value = details as { items?: unknown; skuIds?: unknown };
  const direct = Array.isArray(value.skuIds)
    ? value.skuIds.filter((item): item is string => typeof item === 'string')
    : [];
  const nested = errorDetailItems(details)
    .map((item) => item.skuId)
    .filter((item): item is string => typeof item === 'string');
  return [...new Set([...direct, ...nested])];
}

export function DealerApp({ user, onLogout }: { user: SessionUser; onLogout: () => void }) {
  const canViewCatalog = hasDealerPermission(user, 'catalog.read');
  const canOrder = hasDealerPermission(user, 'orders.create');
  const canViewOrders = hasDealerPermission(user, 'orders.read');
  const canManageAddresses = hasDealerPermission(user, 'addresses.read');
  const [tab, setTab] = useState<DealerTab>(() => canViewCatalog ? 'catalog' : canViewOrders ? 'orders' : canManageAddresses ? 'addresses' : 'none');
  const visibleTab = (tab === 'catalog' && canViewCatalog) || (tab === 'orders' && canViewOrders) || (tab === 'addresses' && canManageAddresses)
    ? tab : canViewCatalog ? 'catalog' : canViewOrders ? 'orders' : canManageAddresses ? 'addresses' : 'none';
  const [categories, setCategories] = useState<CatalogCategory[]>([]);
  const [categoryNodes, setCodeCategories] = useState<CodeCategoryNode[] | undefined>();
  const codeCategories = useSynchronizedCodeCategories(categoryNodes, 'catalog');
  const [products, setProducts] = useState<Product[]>([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalSkus, setTotalSkus] = useState(0);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [history, setHistory] = useState<DealerOrderHistory | null>(null);
  const [ordersRequestedPage, setOrdersRequestedPage] = useState(1);
  const [cart, setCart] = useState<Map<string, CartLine>>(new Map());
  const [categoryId, setCategoryId] = useState('all');
  const [mobileAllProducts, setMobileAllProducts] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const categoryToggleRef = useRef<HTMLButtonElement>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState('');
  const [catalogRetryKey, setCatalogRetryKey] = useState(0);
  const [loadingMoreKey, setLoadingMoreKey] = useState<string | null>(null);
  const [priceRefreshing, setPriceRefreshing] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [addressOpen, setAddressOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const isPhone = usePhoneLayout();
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState<'SELF_PICKUP' | 'DELIVERY'>(() => canManageAddresses ? 'DELIVERY' : 'SELF_PICKUP');
  const [note, setNote] = useState('');
  const [draftHydrated, setDraftHydrated] = useState(false);
  const [draftStatus, setDraftStatus] = useState<DraftStatus>('loading');
  const [draftLoadRetryKey, setDraftLoadRetryKey] = useState(0);
  const [draftRetryKey, setDraftRetryKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string; tone: 'success' | 'error' | 'info' } | null>(null);
  const productSearch = useProductSearch({ onCommit: () => { setLoading(true); setLoadError(''); } });
  const catalogQueryKey = `${categoryId}\u0000${productSearch.query}\u0000${productSearch.revision}\u0000${catalogRetryKey}\u0000${isPhone}`;
  const loadingMore = loadingMoreKey === catalogQueryKey;
  const catalogQueryKeyRef = useRef(catalogQueryKey);
  const catalogMoreAbortRef = useRef<AbortController | null>(null);
  const catalogRefreshAbortRef = useRef<AbortController | null>(null);
  const catalogLoadedPageRef = useRef(1);
  const draftRevisionRef = useRef(0);
  const lastSavedDraftRef = useRef('');
  const latestDraftAttemptRef = useRef(0);
  const suppressNextDraftSaveRef = useRef(false);
  const loggingOutRef = useRef(false);
  const idempotencyKeyRef = useRef(crypto.randomUUID());
  const ordersAttemptRef = useRef(0);
  const ordersAbortRef = useRef<AbortController | null>(null);
  const priceAttemptRef = useRef(0);
  const priceAbortRef = useRef<AbortController | null>(null);
  const lastPriceRefreshRef = useRef(0);

  const loadOrders = useCallback(async (page = 1) => {
    if (!canViewOrders) return;
    const attempt = ++ordersAttemptRef.current;
    ordersAbortRef.current?.abort();
    const controller = new AbortController();
    ordersAbortRef.current = controller;
    setOrdersRequestedPage(page);
    setOrdersLoading(true);
    setOrdersError('');
    try {
      const result = await request<DealerOrderHistory>(`/api/v1/orders/mine?page=${page}&pageSize=${ORDERS_PAGE_SIZE}`, { signal: controller.signal });
      if (attempt !== ordersAttemptRef.current) return;
      setHistory(result);
    } catch (error) {
      if (attempt !== ordersAttemptRef.current) return;
      setOrdersError(error instanceof ApiError ? error.message : '订单加载失败');
    } finally {
      if (attempt === ordersAttemptRef.current) setOrdersLoading(false);
    }
  }, [canViewOrders]);

  useEffect(() => {
    let active = true;
    if (visibleTab === 'orders') queueMicrotask(() => { if (active) void loadOrders(); });
    return () => {
      active = false;
      ordersAttemptRef.current += 1;
      ordersAbortRef.current?.abort();
    };
  }, [loadOrders, visibleTab]);

  useEffect(() => {
    lastPriceRefreshRef.current = Date.now();
    return () => {
      ordersAttemptRef.current += 1;
      ordersAbortRef.current?.abort();
      priceAttemptRef.current += 1;
      priceAbortRef.current?.abort();
    };
  }, []);

  const applySkuPrices = useCallback((skus: Sku[], unavailableIds: string[] = []) => {
    const prices = new Map(skus.map((sku) => [sku.id, sku]));
    setCart((current) => {
      let changed = false;
      const next = new Map(current);
      for (const skuId of unavailableIds) {
        if (next.delete(skuId)) changed = true;
      }
      for (const [skuId, line] of next) {
        const pricing = prices.get(skuId);
        if (!pricing || pricingSignature(pricing) === pricingSignature(line.sku)) continue;
        const sku = updatedSkuPrice(line.sku, pricing);
        next.set(skuId, {
          ...line, sku,
          product: { ...line.product, skus: line.product.skus.map((value) => value.id === skuId ? sku : value) }
        });
        changed = true;
      }
      return changed ? next : current;
    });
  }, []);

  const refreshPrices = useCallback(async (force = false) => {
    if ((!canViewCatalog && !canOrder) || loggingOutRef.current || (!force && submitting)) return;
    if (!force && Date.now() - lastPriceRefreshRef.current < PRICE_REFRESH_INTERVAL_MS) return;
    lastPriceRefreshRef.current = Date.now();
    const attempt = ++priceAttemptRef.current;
    priceAbortRef.current?.abort();
    const controller = new AbortController();
    priceAbortRef.current = controller;
    setPriceRefreshing(true);
    if (canViewCatalog && force) {
      setLoading(true);
      setLoadError('');
      setCatalogRetryKey((value) => value + 1);
    }
    try {
      if (canOrder && draftHydrated) {
        const draft = await request<CartDraft>('/api/v1/cart-draft', { signal: controller.signal });
        if (attempt !== priceAttemptRef.current || loggingOutRef.current) return;
        // Refresh prices only. Unsaved quantities, addresses and notes stay intact.
        applySkuPrices(draft.items.map(draftItemSku), draft.unavailableSkuIds);
      }
    } catch (error) {
      if (attempt !== priceAttemptRef.current) return;
      setToast({ message: error instanceof ApiError ? error.message : '价格刷新失败，请重试；提交时会重新核对售价', tone: 'error' });
    } finally {
      if (attempt === priceAttemptRef.current) setPriceRefreshing(false);
    }
  }, [applySkuPrices, canOrder, canViewCatalog, draftHydrated, submitting]);

  useEffect(() => {
    const refresh = () => { if (document.visibilityState === 'visible') void refreshPrices(); };
    const timer = window.setInterval(refresh, PRICE_REFRESH_INTERVAL_MS);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [refreshPrices]);

  useEffect(() => {
    if (!canOrder && !canManageAddresses) return;
    let active = true;
    void Promise.all([
      canManageAddresses ? request<{ addresses: Address[] }>('/api/v1/addresses') : Promise.resolve({ addresses: [] as Address[] }),
      canOrder ? request<CartDraft>('/api/v1/cart-draft') : Promise.resolve(null)
    ]).then(([addressResult, draft]) => {
      if (!active) return;
      setAddresses(addressResult.addresses);
      const defaultAddress = addressResult.addresses.find((address) => address.isDefault)
        ?? addressResult.addresses[0];
      if (!draft) {
        setSelectedAddressId(defaultAddress?.id ?? '');
        return;
      }
      const restoredAddressId = draft.selectedAddressId
        && (!canManageAddresses || addressResult.addresses.some((address) => address.id === draft.selectedAddressId))
        ? draft.selectedAddressId
        : defaultAddress?.id ?? '';
      const restoredCart = new Map<string, CartLine>();
      for (const item of draft.items) {
        const sku = draftItemSku(item);
        const product: Product = {
          id: item.productId,
          code: item.productCode,
          name: item.productName,
          description: item.description,
          imageUrl: item.imageUrl,
          categoryId: item.categoryId,
          categoryName: item.categoryName,
          skus: [sku]
        };
        restoredCart.set(item.skuId, { product, sku, quantity: item.quantity });
      }
      const payload = currentDraftPayload(restoredCart, restoredAddressId, draft.note);
      draftRevisionRef.current = draft.revision;
      const draftNeedsRepair = draft.unavailableSkuIds.length > 0
        || (draft.selectedAddressId ?? '') !== restoredAddressId;
      lastSavedDraftRef.current = draftNeedsRepair ? '' : draftSignature(payload);
      setSelectedAddressId(restoredAddressId);
      setNote(draft.note);
      setCart(restoredCart);
      setDraftHydrated(true);
      setDraftStatus('saved');
      if (draft.unavailableSkuIds.length > 0) {
        setToast({ message: '草稿中的已下架规格已自动移除', tone: 'info' });
      }
    }).catch((error: unknown) => {
      if (!active) return;
      setDraftHydrated(false);
      setDraftStatus('error');
      setToast({ message: error instanceof ApiError ? error.message : canOrder ? '订购草稿与收货地址加载失败' : '收货地址加载失败', tone: 'error' });
    });
    return () => {
      active = false;
    };
  }, [canManageAddresses, canOrder, draftLoadRetryKey]);

  useEffect(() => {
    if (!canViewCatalog) return;
    let active = true;
    const controller = new AbortController();
    catalogQueryKeyRef.current = catalogQueryKey;
    catalogRefreshAbortRef.current?.abort();
    void requestDealerCatalog(catalogPagePath(productSearch.query, categoryId, 1, isPhone), controller.signal)
      .then((result) => {
        if (!active) return;
        setLoadError('');
        setLoadingMoreKey(null);
        setCategories(result.categories);
        setCodeCategories(result.codeCategories);
        setProducts(result.products);
        catalogLoadedPageRef.current = 1;
        setTotalProducts(result.pagination.totalProducts);
        setTotalSkus(result.pagination.totalSkus ?? result.pagination.totalProducts);
        applySkuPrices(result.products.flatMap((product) => product.skus));
      })
      .catch((error: unknown) => {
        if (active) setLoadError(error instanceof ApiError ? error.message : '商品目录加载失败');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
      catalogMoreAbortRef.current?.abort();
      catalogRefreshAbortRef.current?.abort();
    };
  }, [canViewCatalog, catalogQueryKey, categoryId, productSearch.query, applySkuPrices, isPhone]);

  useCatalogRefresh(() => {
    if (!canViewCatalog || visibleTab !== 'catalog' || loading || productSearch.pending || loadingMore || submitting || loggingOutRef.current) return;
    void refreshCatalogProducts();
  });

  useEffect(() => {
    if (!canOrder || !draftHydrated || loggingOutRef.current || submitting) return;
    const payload = currentDraftPayload(cart, selectedAddressId, note);
    const signature = draftSignature(payload);
    if (suppressNextDraftSaveRef.current) {
      suppressNextDraftSaveRef.current = false;
      lastSavedDraftRef.current = signature;
      setDraftStatus('saved');
      return;
    }
    if (signature === lastSavedDraftRef.current) return;

    const attempt = latestDraftAttemptRef.current + 1;
    latestDraftAttemptRef.current = attempt;
    setDraftStatus('saving');
    const timer = window.setTimeout(() => {
      if (loggingOutRef.current) return;
      const revision = draftRevisionRef.current + 1;
      draftRevisionRef.current = revision;
      void request<{ revision: number; updatedAt: string }>('/api/v1/cart-draft', {
        method: 'PUT',
        body: JSON.stringify({ ...payload, revision })
      }).then((result) => {
        draftRevisionRef.current = Math.max(draftRevisionRef.current, result.revision);
        if (attempt !== latestDraftAttemptRef.current) return;
        lastSavedDraftRef.current = signature;
        setDraftStatus('saved');
      }).catch((error: unknown) => {
        if (attempt !== latestDraftAttemptRef.current) return;
        if (error instanceof ApiError && error.code === 'DRAFT_SKU_UNAVAILABLE') {
          const affected = new Set(errorSkuIds(error.details));
          setCart((current) => {
            const next = new Map(current);
            for (const skuId of affected) next.delete(skuId);
            return next;
          });
          setToast({ message: '部分规格已下架，已从订购单移除', tone: 'info' });
        } else if (error instanceof ApiError && error.code === 'DRAFT_STALE') {
          setDraftHydrated(false);
          setToast({ message: '订购单已在其他页面更新，请刷新后再编辑', tone: 'error' });
        }
        setDraftStatus('error');
      });
    }, 500);
    return () => window.clearTimeout(timer);
  }, [canOrder, cart, draftHydrated, draftRetryKey, note, selectedAddressId, submitting]);

  async function openOrders() {
    if (!canViewOrders) return;
    setTab('orders');
    if (visibleTab === 'orders') await loadOrders();
  }

  const visibleCategoryId = categoryId;
  const selectedCategory = codeCategories?.find((node) => `code:${node.prefix}` === visibleCategoryId);
  const selectedCategoryName = selectedCategory
    ? `${selectedCategory.path} · ${selectedCategory.name ?? `第 ${selectedCategory.depth} 级分类`}`
    : categoryId === 'all' ? '全部商品' : categoryId.startsWith('code:')
      ? `${categoryId.slice(5).match(/.{2}/g)?.join(' / ')} · 当前分类`
      : categories.find((category) => category.id === categoryId)?.name ?? '当前分类';
  const selectedCategoryLabel = `${selectedCategoryName}${productSearch.query ? ' · 搜索结果' : ''}`;
  const browsingCategories = isPhone && !mobileAllProducts && !productSearch.query && (codeCategories?.length ?? 0) > 0;
  const childCategoryCount = codeCategories?.filter(node => node.parentPrefix === (selectedCategory?.prefix ?? null)).length ?? 0;
  const hideRootProducts = browsingCategories && categoryId === 'all';

  function selectCatalogCategory(value: string, keepOpen = false) {
    const nextCategoryId = value || 'all';
    if (categoryOpen && !keepOpen) categoryToggleRef.current?.focus();
    if (!keepOpen) setCategoryOpen(false);
    if (categoryId === nextCategoryId) return;
    if (isPhone) setProducts([]);
    setLoading(true);
    setLoadError('');
    setCategoryId(nextCategoryId);
    if (isPhone) window.scrollTo({ top: 0, behavior: 'instant' });
  }

  async function loadMoreProducts() {
    if (!canViewCatalog || loading || productSearch.pending || loadingMore) return;
    const requestKey = catalogQueryKey;
    const controller = new AbortController();
    catalogRefreshAbortRef.current?.abort();
    catalogMoreAbortRef.current?.abort();
    catalogMoreAbortRef.current = controller;
    setLoadingMoreKey(requestKey);
    try {
      const nextPage = catalogLoadedPageRef.current + 1;
      const result = await requestDealerCatalog(catalogPagePath(productSearch.query, categoryId, nextPage, isPhone), controller.signal);
      if (controller.signal.aborted || catalogQueryKeyRef.current !== requestKey) return;
      catalogLoadedPageRef.current = nextPage;
      setProducts((current) => {
        const merged = new Map(current.map((product) => [product.id, product]));
        for (const product of result.products) merged.set(product.id, product);
        return [...merged.values()];
      });
      setTotalProducts(result.pagination.totalProducts);
      setTotalSkus(result.pagination.totalSkus ?? result.pagination.totalProducts);
      applySkuPrices(result.products.flatMap((product) => product.skus));
    } catch (error) {
      if (controller.signal.aborted || catalogQueryKeyRef.current !== requestKey) return;
      setToast({ message: error instanceof ApiError ? error.message : '更多商品加载失败', tone: 'error' });
    } finally {
      if (!controller.signal.aborted && catalogQueryKeyRef.current === requestKey) setLoadingMoreKey(null);
    }
  }

  async function refreshCatalogProducts() {
    const requestKey = catalogQueryKey;
    const controller = new AbortController();
    catalogRefreshAbortRef.current?.abort();
    catalogRefreshAbortRef.current = controller;
    const pagesToRefresh = catalogLoadedPageRef.current;
    try {
      let result: CatalogResponse | undefined;
      let loadedPage = 0;
      const refreshed = new Map<string, Product>();
      for (let page = 1; page <= pagesToRefresh; page += 1) {
        result = await requestDealerCatalog(catalogPagePath(productSearch.query, categoryId, page, isPhone), controller.signal);
        if (controller.signal.aborted || catalogQueryKeyRef.current !== requestKey) return;
        for (const product of result.products) refreshed.set(product.id, product);
        loadedPage = page;
        if (!result.pagination.hasMore) break;
      }
      if (!result) return;
      // Replace the complete loaded range so new, removed and regrouped SKUs agree with the server.
      const latestProducts = [...refreshed.values()];
      catalogLoadedPageRef.current = loadedPage;
      setProducts(latestProducts);
      setCategories(result.categories);
      setCodeCategories(result.codeCategories);
      setTotalProducts(result.pagination.totalProducts);
      setTotalSkus(result.pagination.totalSkus ?? result.pagination.totalProducts);
      setLoadError('');
      applySkuPrices(latestProducts.flatMap((product) => product.skus));
    } catch {
      // Keep the visible catalog and quantities through a temporary background failure.
    } finally {
      if (catalogRefreshAbortRef.current === controller) catalogRefreshAbortRef.current = null;
    }
  }

  const cartLines = [...cart.values()];
  const cartTotal = cartLines.reduce((sum, line) => sum + line.quantity * line.sku.priceCents, 0);

  function setQuantity(product: Product, sku: Sku, quantity: number) {
    if (!canOrder) return;
    setCart((current) => {
      const next = new Map(current);
      if (quantity <= 0) next.delete(sku.id);
      else next.set(sku.id, { product, sku, quantity: Math.min(999_999, Math.floor(quantity)) });
      return next;
    });
  }

  function startCheckout() {
    if (!canOrder) return;
    if (deliveryMethod === 'DELIVERY') selectDeliveryMethod('DELIVERY');
    setCartOpen(false);
    setCheckoutOpen(true);
  }

  function selectDeliveryMethod(method: 'SELF_PICKUP' | 'DELIVERY') {
    setDeliveryMethod(method);
    if (method === 'DELIVERY') {
      setSelectedAddressId((addresses.find((address) => address.isDefault) ?? addresses[0])?.id ?? '');
    }
  }

  async function submitOrder() {
    if (!canOrder) return;
    if (deliveryMethod === 'DELIVERY' && (!canManageAddresses || !selectedAddressId)) {
      setToast({ message: '请先添加并选择收货地址', tone: 'error' });
      return;
    }
    const currentSignature = draftSignature(currentDraftPayload(cart, selectedAddressId, note));
    if (draftStatus !== 'saved' || currentSignature !== lastSavedDraftRef.current) {
      setToast({ message: '订购单正在保存，请稍后再提交', tone: 'info' });
      return;
    }
    setSubmitting(true);
    priceAttemptRef.current += 1;
    priceAbortRef.current?.abort();
    setPriceRefreshing(false);
    try {
      const body = {
        deliveryMethod,
        ...(deliveryMethod === 'DELIVERY' ? { addressId: selectedAddressId } : {}),
        note,
        idempotencyKey: idempotencyKeyRef.current,
        items: cartLines.map((line) => ({
          skuId: line.sku.id,
          quantity: line.quantity,
          expectedUnitPriceCents: line.sku.priceCents
        }))
      };
      const result = await request<{ order: { orderNo: string } }>('/api/v1/orders', {
        method: 'POST', body: JSON.stringify(body)
      });
      suppressNextDraftSaveRef.current = true;
      draftRevisionRef.current += 1;
      setCart(new Map());
      setNote('');
      setCheckoutOpen(false);
      idempotencyKeyRef.current = crypto.randomUUID();
      setToast({ message: `订单 ${result.order.orderNo} 已提交`, tone: 'success' });
      if (canViewOrders) {
        if (visibleTab === 'orders') await loadOrders();
        setTab('orders');
      }
      else setTab(canViewCatalog ? 'catalog' : canManageAddresses ? 'addresses' : 'none');
    } catch (error) {
      if (error instanceof ApiError && error.code === 'PRICE_CHANGED') {
        const changes = errorDetailItems(error.details)
          .filter((item): item is Record<string, unknown> & { skuId: string; newPriceCents: number } =>
            typeof item.skuId === 'string' && typeof item.newPriceCents === 'number');
        const newPrices = new Map(changes.map((item) => [item.skuId, item.newPriceCents]));
        if (newPrices.size > 0) {
          setCart((current) => {
            const next = new Map(current);
            for (const [skuId, priceCents] of newPrices) {
              const line = next.get(skuId);
              if (!line) continue;
              const sku = updatedSkuPrice(line.sku, { priceCents });
              const product = {
                ...line.product,
                skus: line.product.skus.map((value) => value.id === skuId ? sku : value)
              };
              next.set(skuId, { ...line, sku, product });
            }
            return next;
          });
          setProducts((current) => current.map((product) => ({
            ...product,
            skus: product.skus.map((sku) => newPrices.has(sku.id)
              ? updatedSkuPrice(sku, { priceCents: newPrices.get(sku.id)! })
              : sku)
          })));
        }
        await refreshPrices(true);
        setToast({ message: `已更新 ${Math.max(newPrices.size, 1)} 个规格的价格，请重新确认`, tone: 'info' });
        setCheckoutOpen(false);
        setCartOpen(true);
      } else if (error instanceof ApiError && error.code === 'SKU_UNAVAILABLE') {
        const affected = new Set(errorSkuIds(error.details));
        if (affected.size > 0) {
          setCart((current) => {
            const next = new Map(current);
            for (const skuId of affected) next.delete(skuId);
            return next;
          });
        }
        setToast({ message: `已移除 ${Math.max(affected.size, 1)} 个下架规格，其余商品已保留`, tone: 'error' });
        setCheckoutOpen(false);
        setCartOpen(true);
      } else {
        setToast({ message: error instanceof ApiError ? error.message : '订单提交失败', tone: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function logout() {
    loggingOutRef.current = true;
    try {
      if (canOrder && draftHydrated) {
        const payload = currentDraftPayload(cart, selectedAddressId, note);
        if (draftSignature(payload) !== lastSavedDraftRef.current) {
          const revision = draftRevisionRef.current + 1;
          draftRevisionRef.current = revision;
          await request('/api/v1/cart-draft', {
            method: 'PUT',
            body: JSON.stringify({ ...payload, revision })
          }).catch(() => undefined);
        }
      }
      await request('/api/v1/auth/logout', { method: 'POST' });
      onLogout();
    } catch (error) {
      loggingOutRef.current = false;
      setToast({ message: error instanceof ApiError ? error.message : '退出失败，请重试', tone: 'error' });
    }
  }

  return (
    <div className={`dealer-shell${canOrder && cartLines.length > 0 && visibleTab === 'catalog' ? ' dealer-shell--has-cart' : ''}`}>
      <header className="dealer-header">
        <BrandMark />
        <nav aria-label="经销商主导航">
          {canViewCatalog && <button className={visibleTab === 'catalog' ? 'active' : ''} aria-current={visibleTab === 'catalog' ? 'page' : undefined} onClick={() => setTab('catalog')}><Package />{canOrder ? '订购商品' : '商品目录'}</button>}
          {canViewOrders && <button className={visibleTab === 'orders' ? 'active' : ''} aria-current={visibleTab === 'orders' ? 'page' : undefined} onClick={() => void openOrders()}><ClockCounterClockwise />我的订单</button>}
          {canManageAddresses && <button className={visibleTab === 'addresses' ? 'active' : ''} aria-current={visibleTab === 'addresses' ? 'page' : undefined} onClick={() => setTab('addresses')}><AddressBook />收货地址</button>}
          {canOrder && isPhone && <button className="dealer-mobile-cart-entry" onClick={() => setCartOpen(true)} aria-label={`购物车，已选 ${cartLines.length} 个品种`}><ShoppingCart /><span>购物车{cartLines.length > 0 ? ` (${cartLines.length})` : ''}</span></button>}
        </nav>
        <div className="dealer-header__account">
          <span><UserCircle /><i><strong>{user.customerName}</strong><small>{user.displayName}</small></i></span>
          <Button variant="ghost" onClick={() => setPasswordOpen(true)}>修改我的密码</Button>
          <button className="icon-button" onClick={() => void logout()} aria-label="退出登录"><SignOut /></button>
        </div>
        {isPhone && <MobileHeaderControls onAccount={() => setAccountOpen(true)} />}
      </header>

      {visibleTab === 'catalog' ? (
        <main className="dealer-main dealer-catalog-main">
          {isPhone && <section className="catalog-controls dealer-mobile-search" aria-label="查找订购商品"><ProductSearch search={productSearch} busy={loading} /></section>}
          <DealerCatalogLayout enabled>
          <aside id="dealer-category-panel" className="category-navigation dealer-category-navigation" aria-label="订购商品分类">
            {isPhone ? <MobileCategoryPicker nodes={codeCategories ?? []} legacyCategories={categories} selectedValue={visibleCategoryId}
              compact={Boolean(productSearch.query) || mobileAllProducts} showAllProducts={mobileAllProducts && !productSearch.query}
              onShowAllProducts={() => { productSearch.reset(''); setMobileAllProducts(true); selectCatalogCategory('all'); }}
              onSelect={value => { productSearch.reset(''); setMobileAllProducts(false); selectCatalogCategory(value); }} /> : <>
            <button ref={categoryToggleRef} className="dealer-category-toggle" aria-label={`商品分类：${selectedCategoryLabel}`} aria-expanded={categoryOpen} aria-controls="dealer-category-options" onClick={() => setCategoryOpen((open) => !open)}>
              <span><strong>商品分类</strong><small>{selectedCategoryLabel}</small></span><CaretDown aria-hidden="true" />
            </button>
            <div id="dealer-category-options" className={categoryOpen && !isPhone ? 'dealer-category-options is-open' : 'dealer-category-options'}>
              {!isPhone && <ProductCategoryFilter nodes={codeCategories ?? []} legacyCategories={categories} value={visibleCategoryId} onChange={selectCatalogCategory} allProductsInToolbar />}
            </div>
            </>}
          </aside>
          <div id="dealer-product-panel" className="dealer-catalog-content">
          <div className="catalog-result-meta dealer-catalog-summary">
          <div className="dealer-catalog-summary__details">
          <div className="dealer-selected-category">
            <h2>{isPhone ? productSearch.query ? '搜索结果' : hideRootProducts ? '按分类查找商品' : childCategoryCount > 0 ? '本级直属规格' : '选择规格与数量' : selectedCategoryLabel}</h2>
            {selectedCategory && !isPhone && <p>当前分类及其下级商品。</p>}
            {visibleCategoryId.startsWith('code:') && !selectedCategory && <p role="status">当前分类已无可用商品，请清除分类筛选或选择其他分类。</p>}
            {visibleCategoryId !== 'all' && !isPhone && <button onClick={() => selectCatalogCategory('all')}>清除分类筛选</button>}
          </div>

            <p className="catalog-result-count" aria-live="polite">{loading || productSearch.pending ? '正在加载商品…' : loadError ? '商品目录暂不可用' : hideRootProducts ? '逐级打开分类，或输入编号查找' : browsingCategories && childCategoryCount > 0 && totalSkus === 0 ? '请选择下级分类查看规格' : isPhone || productSearch.query ? `找到 ${totalSkus} 个规格，已显示 ${products.reduce((count, product) => count + product.skus.length, 0)} 个` : `找到 ${totalProducts} 个商品，已显示 ${products.length} 个`}</p>
          </div>
            {priceRefreshing && <span className="sr-only" role="status">正在更新单价…</span>}
            {canOrder && <div className="catalog-order-summary">
              <span className={`draft-status draft-status--${draftStatus}`} role="status">{draftStatus === 'loading' ? '正在恢复订购单…' : draftStatus === 'saving' ? '正在保存订购单…' : draftStatus === 'saved' ? '订购单已保存' : <button onClick={() => { setDraftStatus('loading'); if (draftHydrated) setDraftRetryKey((value) => value + 1); else setDraftLoadRetryKey((value) => value + 1); }}>保存失败，重试</button>}</span>
              <button className="cart-summary" onClick={() => setCartOpen(true)} aria-label={`购物车，已选 ${cartLines.length} 个品种`}>
                <span><ShoppingCart weight="fill" /><b>{cartLines.length}</b></span>
                <i><small>购物车 · {cartLines.length} 个品种</small><strong>{formatCny(cartTotal)}</strong></i>
              </button>
            </div>}
          </div>
          {!isPhone && <section className="catalog-controls" aria-label="查找订购商品">
            <ProductSearch search={productSearch} busy={loading} />
          </section>}
          {loading && products.length === 0 ? <div className="loading-block" role="status" aria-label="正在加载商品"><span className="skeleton" /><span className="skeleton" /><span className="skeleton" /></div> : loadError ? (
            <LoadError title="商品目录暂时无法加载" description={loadError} onRetry={() => { setLoading(true); setLoadError(''); setCatalogRetryKey((value) => value + 1); }} />
          ) : hideRootProducts || (browsingCategories && childCategoryCount > 0 && products.length === 0) ? null : products.length === 0 ? (
            <EmptyState title="没有找到匹配商品" description="请尝试更换商品名称、SKU、规格或分类。" />
          ) : (
            <div className="catalog-results" aria-busy={loading || productSearch.pending || loadingMore}>
              {isPhone ? <MobileSkuList products={products} quantityForSku={sku => cart.get(sku.id)?.quantity ?? 0} onQuantity={setQuantity} canOrder={canOrder}
                disabled={loading || productSearch.pending} renderPrice={sku => <SkuPrice sku={sku} showUnit />} /> : <section className="product-list">
                {products.map((product) => <CatalogProductRow key={product.id} product={product} showSkuCode={false} quantityForSku={(sku) => cart.get(sku.id)?.quantity ?? 0} canOrder={canOrder} onQuantity={setQuantity} disabled={() => loading || productSearch.pending} renderMetric={(sku) => <SkuPrice sku={sku} />} />)}
              </section>}
              {products.length < totalProducts && (
                <div className="product-list__footer">
                  <Button variant="secondary" loading={loadingMore} disabled={loading || productSearch.pending} onClick={() => void loadMoreProducts()}>
                    继续加载（剩余 {totalProducts - products.length} 个）
                  </Button>
                </div>
              )}
            </div>
          )}
          </div>
          </DealerCatalogLayout>
        </main>
      ) : visibleTab === 'orders' ? (
        <DealerOrders history={history} loading={ordersLoading} error={ordersError} onRetry={() => void loadOrders(ordersRequestedPage)} onPage={(page) => void loadOrders(page)} {...(canViewCatalog ? { onShop: () => setTab('catalog') } : {})} canOrder={canOrder} />
      ) : visibleTab === 'addresses' ? (
        <main className="dealer-main">
          <section className="page-heading"><div><p className="eyebrow">DELIVERY ADDRESSES</p><h1>收货地址</h1><p>查看并新增本账号的收货地址。</p></div><Button onClick={() => setAddressOpen(true)}><Plus />新增地址</Button></section>
          <div className="address-options">{addresses.map((address) => <article key={address.id}><h2>{address.label}{address.isDefault && <small> · 默认</small>}</h2><p>{address.contactName} · {address.phone}</p><p>{address.province}{address.city}{address.district}{address.detail}</p></article>)}</div>
          {addresses.length === 0 && <EmptyState title="还没有收货地址" description="新增地址后，可在订购时选择收货地点。" />}
        </main>
      ) : (
        <main className="dealer-main"><EmptyState title="当前账号暂未开放业务功能" description="请联系管理员配置商品、订单或收货地址权限。" /></main>
      )}

      {canOrder && visibleTab === 'catalog' && <button className="mobile-cart-bar" onClick={() => setCartOpen(true)} disabled={cartLines.length === 0}>
        <span><ShoppingCart weight="fill" /><b>{cartLines.length}</b> 个品种 · 查看订购单</span><Money cents={cartTotal} />
      </button>}

      {canOrder && cartOpen && (
        <Modal className="cart-modal" initialFocus="dialog" title="本次订购单" description={`共 ${cartLines.length} 个品种`} onClose={() => setCartOpen(false)} footer={<><div className="modal-total"><span>商品合计<small>不含运费</small></span><Money cents={cartTotal} /></div><Button onClick={startCheckout} disabled={cartLines.length === 0}>确认收货信息</Button></>}>
          {cartLines.length === 0 ? <EmptyState title="订购单还是空的" description="请从商品目录选择规格并填写数量。" /> : <div className="cart-lines">{cartLines.map((line) => <CartLineRow key={line.sku.id} line={line} compact={isPhone} onQuantity={(value) => setQuantity(line.product, line.sku, value)} />)}</div>}
        </Modal>
      )}

      {canOrder && checkoutOpen && (
        <Modal title="确认订单" description="提交后将形成正式订单，客户不能自行修改或取消。" onClose={() => !submitting && setCheckoutOpen(false)} footer={<><Button variant="secondary" onClick={() => setCheckoutOpen(false)} disabled={submitting}><ArrowLeft />返回修改</Button><Button onClick={() => void submitOrder()} loading={submitting} disabled={draftStatus !== 'saved'}>{draftStatus === 'error' ? '草稿保存失败' : draftStatus === 'saved' ? '提交正式订单' : '正在保存订购单…'}</Button></>}>
          <fieldset className="checkout-delivery-options" disabled={submitting}>
            <legend>取货方式</legend>
            <label className={deliveryMethod === 'SELF_PICKUP' ? 'selected' : ''}><input type="radio" name="delivery-method" value="SELF_PICKUP" checked={deliveryMethod === 'SELF_PICKUP'} onChange={() => selectDeliveryMethod('SELF_PICKUP')} /><span><b>到店自取</b><small>到店取货，无需收货地址</small></span></label>
            <label className={deliveryMethod === 'DELIVERY' ? 'selected' : ''}><input type="radio" name="delivery-method" value="DELIVERY" checked={deliveryMethod === 'DELIVERY'} disabled={!canManageAddresses} onChange={() => selectDeliveryMethod('DELIVERY')} /><span><b>送货上门</b><small>{canManageAddresses ? '自动选用默认地址，也可新增地址' : '请联系管理员开通收货地址权限'}</small></span></label>
          </fieldset>
          {deliveryMethod === 'DELIVERY' && canManageAddresses && <div className="checkout-section">
            <div className="section-title"><span><AddressBook /></span><div><h3>收货地址</h3><p>可选择其他收货地址，也可新增地址</p></div><button onClick={() => setAddressOpen(true)} disabled={submitting}>新增地址</button></div>
            <div className="address-options">
              {addresses.map((address) => <label key={address.id} className={selectedAddressId === address.id ? 'selected' : ''}><input type="radio" name="address" checked={selectedAddressId === address.id} disabled={submitting} onChange={() => setSelectedAddressId(address.id)} /><span><b>{address.label}{address.isDefault && <em>默认</em>}</b><small>{address.contactName} · {address.phone}</small><p>{address.province}{address.city}{address.district}{address.detail}</p></span><Check weight="bold" /></label>)}
              {addresses.length === 0 && <EmptyState title="还没有收货地址" description="新增一个地址后即可提交订单。" action={<Button variant="secondary" onClick={() => setAddressOpen(true)}>新增地址</Button>} />}
            </div>
          </div>}
          <div className="checkout-section">
            <div className="section-title"><span><Package /></span><div><h3>商品明细</h3><p>提交前请核对规格、数量和单价</p></div></div>
            <ul className="checkout-items">{cartLines.map((line) => <li key={line.sku.id}><span><b>{line.product.name}</b><small>{selectedSkuLabel(line.product, line.sku)}</small></span><span><SkuPrice sku={line.sku} showUnit compact /><b>{line.quantity} {line.sku.unit}</b></span><Money cents={line.sku.priceCents * line.quantity} /></li>)}</ul>
          </div>
          <div className="checkout-section">
            <Field label="订单备注" hint="选填，最多 500 字"><textarea value={note} onChange={(event) => setNote(event.target.value)} maxLength={500} placeholder="例如：请与仓库联系人确认到货时间" /></Field>
          </div>
          <div className="checkout-total"><span><b>{cartLines.length}</b> 个品种</span><span>商品合计 <Money cents={cartTotal} /></span></div>
        </Modal>
      )}

      {canManageAddresses && addressOpen && <AddressModal onClose={() => setAddressOpen(false)} onCreated={(address) => { setAddresses((values) => [address, ...values.map((value) => address.isDefault ? { ...value, isDefault: false } : value)]); setSelectedAddressId(address.id); setAddressOpen(false); }} />}
      {accountOpen && <Modal title="账号设置" onClose={() => setAccountOpen(false)}>
        <div className="dealer-account-settings">
          <div><h3>{user.customerName}</h3><p>{user.displayName}</p></div>
          <FontSizeControl />
          <Button variant="secondary" onClick={() => { setAccountOpen(false); setPasswordOpen(true); }}>修改我的密码</Button>
          <Button variant="ghost" onClick={() => void logout()}><SignOut />退出登录</Button>
        </div>
      </Modal>}
      {passwordOpen && <OwnPasswordModal customerAccount onClose={() => setPasswordOpen(false)} onSaved={() => {
        setPasswordOpen(false);
        setToast({ message: '密码已修改，下次登录请使用新密码', tone: 'success' });
      }} />}
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}

function isGroupedProduct(product: Product): boolean {
  return (product.sourceProductCount ?? 1) > 1;
}

function selectedSkuLabel(product: Product, sku: Sku): string {
  return [
    isGroupedProduct(product) ? sku.productName : null,
    sku.specification,
    sku.code
  ].filter(Boolean).join(' · ');
}

function SkuPrice({ sku, showUnit = false, compact = false }: { sku: Sku; showUnit?: boolean; compact?: boolean }) {
  const discounted = sku.basePriceCents !== undefined && sku.basePriceCents > sku.priceCents;
  const promotion = discounted ? sku.promotion : undefined;
  return (
    <span className={`dealer-sale-price${compact ? ' dealer-sale-price--compact' : ''}`}>
      <span><Money cents={sku.priceCents} />{showUnit && <small>/{sku.unit}</small>}</span>
      {discounted && <span className="dealer-sale-price__offer">
        <s aria-label={`原价 ${formatCny(sku.basePriceCents!)}`}>原价 {formatCny(sku.basePriceCents!)}</s>
        {promotion && <em title={promotion.name}>{Number((promotion.discountPercent / 10).toFixed(2))}折 · {promotion.name}</em>}
      </span>}
    </span>
  );
}

function CartLineRow({ line, compact, onQuantity }: { line: CartLine; compact: boolean; onQuantity: (value: number) => void }) {
  if (compact) {
    const { product, sku, quantity } = line;
    const name = sku.productName || product.name;
    const code = sku.productCode || product.code;
    return <article className="mobile-sku mobile-cart-line">
      <header>
        {(sku.imageUrl || product.imageUrl) && <div className="product-image"><ProductImage productId={sku.productId || product.id} imageUrl={sku.imageUrl || product.imageUrl} /></div>}
        <div className="mobile-sku__identity"><span className="product-code">商品编号 {code}</span><h3>{name}</h3>
          <p>规格：{sku.specification} · 单位：{sku.unit}</p>
          {sku.code !== code && <small>SKU 编号 {sku.code}</small>}
        </div>
        <button className="icon-button danger-text" onClick={() => onQuantity(0)} aria-label={`移出订购单：${name}（${sku.code}）`}><Trash /></button>
      </header>
      <div className="mobile-sku__order"><SkuPrice sku={sku} showUnit />
        <CatalogQuantity value={quantity} unit={sku.unit} label={`${name}（${sku.code}）订购数量，单位${sku.unit}`} onChange={onQuantity} />
      </div>
      <p className="mobile-sku__subtotal">已选 {quantity} {sku.unit}<span>小计 <Money cents={sku.priceCents * quantity} /></span></p>
    </article>;
  }
  return <div className="cart-line"><div><span>{line.product.name}</span><b>{isGroupedProduct(line.product) ? line.sku.productName || line.sku.specification : line.sku.specification}</b><small>{isGroupedProduct(line.product) ? `${line.sku.specification} · ` : ''}{line.sku.code}</small><SkuPrice sku={line.sku} showUnit compact /></div><CatalogQuantity value={line.quantity} unit={line.sku.unit} onChange={onQuantity} /><Money cents={line.sku.priceCents * line.quantity} /><button className="icon-button danger-text" onClick={() => onQuantity(0)} aria-label="移出订购单"><Trash /></button></div>;
}

function DealerOrders({ history, loading, error, onRetry, onPage, onShop, canOrder }: { history: DealerOrderHistory | null; loading: boolean; error: string; onRetry: () => void; onPage: (page: number) => void; onShop?: () => void; canOrder: boolean }) {
  const [openId, setOpenId] = useState<string | null>(null);
  const orders = history?.orders ?? [];
  const pagination = history?.pagination;
  return (
    <main className="dealer-main orders-page">
      <section className="page-heading">
        <div>
          <p className="eyebrow">ORDER STATUS</p>
          <h1>我的订单</h1>
          <p>保留本账号的全部历史订单，已完结订单也可随时查看。</p>
        </div>
        {onShop && <Button onClick={onShop}><Plus />{canOrder ? '继续订购' : '查看商品'}</Button>}
      </section>
      {history && <section className="dealer-order-history-stats" aria-label="全部历史订单统计" aria-busy={loading}>
        <dl>
          <div><dt>全部订单</dt><dd>{history.stats.totalOrders}<small>单</small></dd></div>
          <div><dt>进行中</dt><dd>{history.stats.activeOrders}<small>单</small></dd></div>
          <div><dt>已完结</dt><dd>{history.stats.completedOrders}<small>单</small></dd></div>
          <div><dt>已取消</dt><dd>{history.stats.cancelledOrders}<small>单</small></dd></div>
          <div className="dealer-order-history-stats__amount"><dt>累计订货金额</dt><dd><Money cents={history.stats.totalAmountCents} /></dd></div>
        </dl>
        <p>统计全部历史记录，累计订货金额不含已取消订单。</p>
      </section>}
      {loading ? (
        <div className="loading-block" role="status" aria-label="正在加载订单"><span className="skeleton" /><span className="skeleton" /><span className="skeleton" /></div>
      ) : error ? (
        <LoadError title="订单记录暂时无法加载" description={`${error}。请检查网络连接后重试。`} onRetry={onRetry} />
      ) : orders.length === 0 ? (
        <EmptyState title="还没有历史订单" description="本账号的订单记录与累计统计将保存在这里。" {...(onShop ? { action: <Button onClick={onShop}>{canOrder ? '继续订购' : '查看商品'}</Button> } : {})} />
      ) : (
        <div className="dealer-order-list">
          {orders.map((order) => {
            const fulfillment = fulfillmentLabel(order.fulfillmentStatus, order.deliveryMethod);
            return (
              <article key={order.id} className="dealer-order">
                <button onClick={() => setOpenId(openId === order.id ? null : order.id)} aria-expanded={openId === order.id}>
                  <span><small>订单号</small><strong>{order.orderNo}</strong></span>
                  <span><small>下单时间</small><b>{formatDateTime(order.createdAt)}</b></span>
                  <span><small>商品件数</small><b>{order.items.reduce((sum, item) => sum + item.quantity, 0)} 件</b></span>
                  <Money cents={order.totalCents} />
                  {order.status === 'CANCELLED'
                    ? <StatusBadge tone="danger">已取消</StatusBadge>
                    : <StatusBadge tone={fulfillment.tone}>{fulfillment.label}</StatusBadge>}
                  <CaretDown className={openId === order.id ? 'rotate' : ''} />
                </button>
                {openId === order.id && (
                  <div className="dealer-order__detail">
                    {order.status === 'CANCELLED' && <div className="cancel-note">取消原因：{order.cancellationReason}</div>}
                    {order.orderSource === 'STAFF' && <p className="order-note">管理员代客下单{order.createdByName ? ` · ${order.createdByName}` : ''}</p>}
                    <p className="address-line">{order.deliveryMethod === 'SELF_PICKUP' ? '到店自提' : '收货'}：{order.addressSnapshot.contactName}{order.addressSnapshot.phone && ` · ${order.addressSnapshot.phone}`}{order.deliveryMethod !== 'SELF_PICKUP' && ` · ${order.addressSnapshot.province}${order.addressSnapshot.city}${order.addressSnapshot.district}${order.addressSnapshot.detail}`}</p>
                    {order.items.map((item) => <div key={item.id}><span><b>{item.productNameSnapshot}</b><small>{item.specificationSnapshot} · {item.skuCodeSnapshot}</small><small>成交单价 {formatCny(item.unitPriceCents)}/{item.unitSnapshot}</small></span><span>{item.quantity} {item.unitSnapshot}</span><Money cents={item.lineTotalCents} /></div>)}
                    {order.note && <p className="order-note">备注：{order.note}</p>}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
      {pagination && pagination.totalOrders > 0 && <nav className="dealer-order-pagination" aria-label="订单历史分页">
        <Button variant="secondary" onClick={() => { setOpenId(null); onPage(pagination.page - 1); }} disabled={loading || pagination.page <= 1}>上一页</Button>
        <span aria-live="polite">第 {pagination.page} / {Math.max(1, pagination.totalPages)} 页 · 共 {pagination.totalOrders} 单</span>
        <Button variant="secondary" onClick={() => { setOpenId(null); onPage(pagination.page + 1); }} disabled={loading || !pagination.hasMore}>下一页</Button>
      </nav>}
    </main>
  );
}

export function AddressModal({ onClose, onCreated, customerId }: { onClose: () => void; onCreated: (address: Address) => void; customerId?: string }) {
  const [form, setForm] = useState({ contactName: '', phone: '', detail: '', isDefault: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const savingRef = useRef(false);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (savingRef.current) return;
    setError('');
    const input = { contactName: form.contactName.trim(), phone: form.phone.trim(), detail: form.detail.trim(), isDefault: form.isDefault };
    if (!input.contactName || !input.phone || !input.detail) {
      setError('请填写联系人、联系电话和详细地址');
      return;
    }
    savingRef.current = true;
    setLoading(true);
    try {
      const path = customerId ? `/api/v1/admin/customers/${customerId}/addresses` : '/api/v1/addresses';
      const result = await request<{ address: Address }>(path, { method: 'POST', body: JSON.stringify(input) });
      onCreated(result.address);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : '地址保存失败');
    } finally {
      savingRef.current = false;
      setLoading(false);
    }
  }
  const update = (key: keyof typeof form, value: string | boolean) => setForm((current) => ({ ...current, [key]: value }));
  return <Modal title="新增收货地址" onClose={() => { if (!savingRef.current) onClose(); }}
    footer={<><Button variant="secondary" onClick={onClose} disabled={loading}>取消</Button><Button type="submit" form="address-form" loading={loading}>保存地址</Button></>}>
    <form id="address-form" className="form-grid" onSubmit={submit}>
      {error && <div className="inline-error form-grid__full" role="alert">{error}</div>}
      <Field label="联系人"><input value={form.contactName} onChange={(e) => update('contactName', e.target.value)} maxLength={80} disabled={loading} required autoFocus /></Field>
      <Field label="联系电话"><input type="tel" value={form.phone} onChange={(e) => update('phone', e.target.value)} maxLength={30} disabled={loading} required /></Field>
      <div className="form-grid__full"><Field label="详细地址"><textarea value={form.detail} onChange={(e) => update('detail', e.target.value)} placeholder="填写完整送货地址" maxLength={300} disabled={loading} required /></Field></div>
      <label className="check-field form-grid__full"><input type="checkbox" checked={form.isDefault} onChange={(e) => update('isDefault', e.target.checked)} disabled={loading} />设为默认收货地址</label>
    </form>
  </Modal>;
}
